/**
 * Town portal link permissions — add new send-dialog options here.
 *
 * HOW TO ADD A FLAG
 * -----------------
 * 1. DB: `ALTER TABLE Propriety.counterparty_links ADD COLUMN your_flag TINYINT(1) NOT NULL DEFAULT 0;`
 * 2. Backend: append key to `COUNTERPARTY_LINK_PERMISSION_KEYS` in
 *    `PythonReqs/src/TownPortal/link_permissions.py` (and `_CHILD_OF` if it depends on a parent).
 * 3. Frontend: append one object below to `TOWN_PORTAL_LINK_PERMISSIONS`.
 * 4. In React UI: `useUser()` and read `user.yourUserKey` (see userKey on each entry below).
 *
 * REGISTRY FIELDS
 * ---------------
 * - apiKey — snake_case key in API / JWT / DB (e.g. `share_workups`)
 * - userKey — camelCase on `user` after link login (e.g. `shareWorkups`)
 * - defaultValue — dialog initial state and JWT fallback when the claim is absent
 * - dialogKind — `toggle` | `childToggle` (UI only)
 * - parentApiKey — for childToggle: disabled in UI when parent is off; parent/child
 *   gating on save and in session is enforced in `link_permissions.py` (`_CHILD_OF`)
 * - dialogLabel, dialogDescription — send-dialog copy
 *
 * Transforms: dialog state → POST body (`buildLinkOptions`), JWT → user (`mapTokenPermissionsToUser`),
 * toggle side-effects (`applyLinkPermissionChange`).
 */

export const TOWN_PORTAL_LINK_PERMISSIONS = [
  {
    apiKey: 'share_workups',
    userKey: 'shareWorkups',
    defaultValue: false,
    dialogKind: 'toggle',
    dialogLabel: 'Share workups',
    dialogDescription: 'Include valuation workups on the portal link',
  },
  {
    apiKey: 'show_one_pagers',
    userKey: 'showOnePagers',
    defaultValue: true,
    dialogKind: 'childToggle',
    parentApiKey: 'share_workups',
    dialogLabel: 'Use One Pagers',
  },
  {
    apiKey: 'combined_file_download',
    userKey: 'combinedFileDownload',
    defaultValue: false,
    dialogKind: 'childToggle',
    parentApiKey: 'share_workups',
    dialogLabel: 'Allow combined workup download',
    dialogDescription: 'Lets the town download all workups as a single PDF',
  },
  {
    apiKey: 'show_grid_comps',
    userKey: 'ShowGridComps',
    defaultValue: false,
    dialogKind: 'toggle',
    dialogLabel: 'Display Comp Addresses',
    dialogDescription: 'Allow town to see our comp addresses in table',
  },
];

/** Update one toggle; parent off clears dependent child flags in dialog state. */
export const applyLinkPermissionChange = (prev, apiKey, value) => {
  const next = { ...prev, [apiKey]: value };
  if (apiKey === 'share_workups') {
    if (value) {
      next.show_one_pagers = true;
    } else {
      next.show_one_pagers = false;
      next.combined_file_download = false;
    }
  }
  return next;
};

/** Registry defaults or saved API/JWT flags → dialog permission state (apiKey keys). */
export const buildPermissionStateFromSaved = (saved = {}) =>
  Object.fromEntries(
    TOWN_PORTAL_LINK_PERMISSIONS.map((p) => {
      const raw = saved[p.apiKey];
      const value = raw === undefined || raw === null ? p.defaultValue : Boolean(raw);
      return [p.apiKey, value];
    }),
  );

/** Dialog state → snake_case bools for POST /auth/counterparty-link. Parent gating on save is in Python. */
export const buildLinkOptions = (input = {}) =>
  Object.fromEntries(
    TOWN_PORTAL_LINK_PERMISSIONS.map((p) => [p.apiKey, Boolean(input[p.apiKey])]),
  );

/** Session JWT (snake_case) → user booleans. Missing claims use registry defaultValue. */
export const mapTokenPermissionsToUser = (tokenPayload = {}) =>
  Object.fromEntries(
    TOWN_PORTAL_LINK_PERMISSIONS.map((p) => {
      const raw = tokenPayload[p.apiKey];
      const value = raw === undefined || raw === null ? p.defaultValue : Boolean(raw);
      return [p.userKey, value];
    }),
  );
